/**
 * Employee Classification Configuration
 *
 * Single source of truth for classification-to-color mapping across the HR Portal.
 * Every classification badge, staff ID chip, and avatar color derives from this file.
 *
 * Color assignments:
 *   managing_director       = red    → top authority
 *   executive_director      = amber  → senior leadership
 *   non_executive_director  = teal   → board-level / oversight
 *   staff                   = blue   → standard office employee
 *   worker                  = green  → operational / field-level
 *   advisor                 = purple → senior / independent consultant
 *   intern                  = cyan   → temporary / learning
 *   outsourced              = orange → external asset holders (JB office)
 *   system                  = gray   → non-human account
 */

export const CLASSIFICATION_CONFIG = {
  managing_director: {
    label: 'Managing Director',
    lightClasses: 'bg-red-50 text-red-700 ring-red-600/20',
    solidClasses: 'bg-red-600 text-white',
    avatarHex: '#dc2626',
  },
  executive_director: {
    label: 'Executive Director',
    lightClasses: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    solidClasses: 'bg-amber-500 text-white',
    avatarHex: '#f59e0b',
  },
  staff: {
    label: 'Staff',
    lightClasses: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    solidClasses: 'bg-blue-800 text-white',
    avatarHex: '#1e3a8a',
  },
  worker: {
    label: 'Worker',
    lightClasses: 'bg-green-50 text-green-700 ring-green-600/20',
    solidClasses: 'bg-green-600 text-white',
    avatarHex: '#16a34a',
  },
  intern: {
    label: 'Intern',
    lightClasses: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20',
    solidClasses: 'bg-cyan-500 text-white',
    avatarHex: '#06b6d4',
  },
  non_executive_director: {
    label: 'Non-Executive Director',
    lightClasses: 'bg-teal-50 text-teal-700 ring-teal-600/20',
    solidClasses: 'bg-teal-600 text-white',
    avatarHex: '#0d9488',
  },
  advisor: {
    label: 'Advisor',
    lightClasses: 'bg-purple-50 text-purple-700 ring-purple-600/20',
    solidClasses: 'bg-purple-600 text-white',
    avatarHex: '#9333ea',
  },
  outsourced: {
    label: 'Outsourced',
    lightClasses: 'bg-orange-50 text-orange-700 ring-orange-600/20',
    solidClasses: 'bg-orange-600 text-white',
    avatarHex: '#ea580c',
  },
  system: {
    label: 'System',
    lightClasses: 'bg-gray-100 text-gray-700 ring-gray-600/20',
    solidClasses: 'bg-gray-500 text-white',
    avatarHex: '#6b7280',
  },
};

/**
 * Classifications that are deemed present and do not clock in/out.
 *
 * Keep aligned with staff_core.models.ATTENDANCE_EXEMPT_CLASSIFICATIONS.
 * The API-provided is_attendance_exempt flag remains authoritative; this
 * classification list is a defensive UI fallback for cached/legacy rows.
 */
export const ATTENDANCE_EXEMPT_CLASSIFICATIONS = Object.freeze([
  'managing_director',
  'executive_director',
  'non_executive_director',
  'advisor',
  'system',
]);

/**
 * Resolve attendance exemption from the API flag, explicit tracking mode,
 * or canonical employment classification.
 *
 * @param {object} employee
 * @returns {boolean}
 */
export function isAttendanceExempt(employee) {
  if (!employee) return false;
  if (employee.is_attendance_exempt === true) return true;
  if ((employee.attendance_tracking_mode || '').toLowerCase() === 'exempt') return true;

  const classification = (employee.employment_classification || '').toLowerCase();
  return ATTENDANCE_EXEMPT_CLASSIFICATIONS.includes(classification);
}

const DEFAULT = CLASSIFICATION_CONFIG.staff;

function formatClassification(key) {
  if (!key) return '';
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getClassificationColor(classification) {
  return (CLASSIFICATION_CONFIG[classification] || DEFAULT).lightClasses;
}

export function getClassificationSolidColor(classification) {
  return (CLASSIFICATION_CONFIG[classification] || DEFAULT).solidClasses;
}

export function getClassificationLabel(classification) {
  return CLASSIFICATION_CONFIG[classification]?.label || formatClassification(classification);
}

export function getClassificationAvatarHex(classification) {
  return (CLASSIFICATION_CONFIG[classification] || DEFAULT).avatarHex;
}
